import styled, { css } from 'styled-components';
import { BLACK_10, BLACK_60, PRIMARY_BASE, SECONDARY_BASE, PRIMARY_ACTIVE } from '../constants/colors';

export type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge';
export type ButtonVariant = 'primary' | 'secondary';

export const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  small: css`
    padding: 6px 12px;
    font-size: 14px;
    line-height: 20px;
  `,
  medium: css`
    padding: 10px 16px;
    font-size: 16px;
    line-height: 22px;
  `,
  large: css`
    padding: 12px 24px;
    font-size: 18px;
    line-height: 24px;
  `,
  xlarge: css`
    padding: 16px 24px;
    font-size: 18px;
    line-height: 24px;
    border-radius: 16px;
  `,
};

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background-color: ${PRIMARY_BASE};
    color: #fff;

    &:focus,
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }

    &:disabled {
      background-color: ${PRIMARY_ACTIVE};
      filter: none;
    }
  `,
  secondary: css`
    background-color: ${SECONDARY_BASE};
    color: ${PRIMARY_BASE};

    &:focus,
    &:hover:not(:disabled) {
      filter: brightness(0.96);
    }

    &:disabled {
      background-color: ${BLACK_10};
      color: ${BLACK_60};
      filter: none;
    }
  `,
};

export const StyledButton = styled.button<{
  $size: ButtonSize;
  $variant: ButtonVariant;
  $fullWidth?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  box-sizing: border-box;
  border-radius: 12px;
  border: none;
  box-shadow: none;
  text-align: center;
  font-weight: bold;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.125s ease-in-out, filter 0.125s ease-in-out;
  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}

  &:disabled {
    cursor: not-allowed;
  }

  .button-loading-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: inherit;
    visibility: hidden;
  }
`;

export const ButtonLoader = styled.span<{ $variant: ButtonVariant }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  ${({ $variant }) =>
    $variant === 'primary' &&
    css`
      filter: brightness(0) invert(1);
    `}
`;
